import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["collapsed", "panelType", "opaqueBackground", "floating", "children"],
  _excluded2 = ["panelInTransition", "children"],
  _excluded3 = ["collapsed", "appearance", "navLabelLayout", "onNavToggle", "contentMarginBlockStart", "navCardMarginBlockStart"];
import { Component } from 'react';
import { CSSTransitionGroup } from '../utils/CSSTransitionGroupCompat';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonGroup } from 'foundations-components/transitional/components/ButtonGroup';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { SidebarLeftCollapseIcon } from 'foundations-assets/icons/components/SidebarLeftCollapseIcon';
import { SidebarLeftExpandIcon } from 'foundations-assets/icons/components/SidebarLeftExpandIcon';
import styled from 'foundations-theming/utils';
import { FLOATY, PUSHY, COLLAPSED, TOGGLED_ANIMATION_DURATION, PANEL, EPSIDE_TOOLBAR_APPEARANCE, EPSIDE_TOOLBAR_NAV_LABEL_LAYOUT, EXPANDED_NAV_INLINE_SIZE, LABEL_BELOW_NAV_INLINE_SIZE, NAV_FLOATING_GAP_VALUE } from '../EPConstants';
import FadingVeil from './FadingVeil';
import { getPatagoniaPanelAnimationType, easeInOutSine, computeUpdatedWidth } from '../utils/animationUtils';
import { getPanelFromSidebarOptions } from '../utils/sidebarUtils';
import { filterProps } from 'foundations-components/utils/filterProps';
import { useMapTokenPropsToCSS } from 'foundations-components/hooks/useMapTokenPropsToCSS';
import { Badge } from 'foundations-components/transitional/components/Badge';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const DEFAULT_EXPANDED_WIDTH = 567;
const getWidthForPanel = (sidebarOptions, panelName) => {
  if (!panelName) {
    return 0;
  }
  const panel = getPanelFromSidebarOptions(sidebarOptions, panelName);
  return panel ? panel.contentWidth : 0;
};
const getIsPanelEmpty = panel => panel === '';
const Sidebar = styled.div.withConfig({
  displayName: "EPSideToolbar__Sidebar"
})(({
  $collapsed,
  $panelType,
  $opaqueBackground,
  $floating,
  theme,
  $cssStyles
}) => Object.assign({
  position: 'relative',
  height: '100%'
}, !$floating && {
  borderRight: `${theme.borderWidth['100']} solid ${theme.color['border-core-subtle']}`
}, {
  zIndex: '5',
  background: $floating ? theme.color['fill-surface-recessed'] : $collapsed || $panelType === PUSHY || $opaqueBackground ? theme.color['fill-surface-default'] : `${theme.color['fill-surface-default']}F2`,
  display: 'flex'
}, $cssStyles));
const SidebarContainer = _ref => {
  let {
      collapsed,
      panelType,
      opaqueBackground,
      floating,
      children
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    tokenProps,
    domProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  // @bend-expect trellis-tokenized
  return /*#__PURE__*/_jsx(Sidebar, Object.assign({
    $collapsed: collapsed,
    $panelType: panelType,
    $opaqueBackground: opaqueBackground,
    $floating: floating,
    $cssStyles: cssStyles
  }, domProps, {
    children: children
  }));
};
const NavToolbarCard = styled.div.withConfig({
  displayName: "EPSideToolbar__NavToolbarCard"
})(({
  $navCardMarginBlockStart,
  theme
}) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  margin: theme.space['200'],
  marginBlockStart: $navCardMarginBlockStart !== null && $navCardMarginBlockStart !== void 0 ? $navCardMarginBlockStart : theme.space['200'],
  background: theme.color['fill-surface-default'],
  borderRadius: theme.borderRadius.full,
  boxShadow: theme.boxShadow['100'],
  border: `${theme.borderWidth['100']} solid ${theme.color['border-core-subtle']}`,
  blockSize: 'fit-content',
  paddingBlock: theme.space['200']
}));
const SidebarContent = styled.div.withConfig({
  displayName: "EPSideToolbar__SidebarContent"
})(({
  collapsed,
  floating,
  floatingActive,
  panelType,
  showScroll,
  opaqueBackground,
  $contentMarginBlockStart,
  theme
}) => {
  const isCollapsedOrFloaty = panelType === COLLAPSED || panelType === FLOATY;
  const isCollapsedOrPushy = panelType === COLLAPSED || panelType === PUSHY;
  const shared = {
    position: isCollapsedOrFloaty ? 'absolute' : 'relative',
    overflowY: showScroll ? 'auto' : 'hidden',
    overflowX: 'hidden',
    zIndex: panelType === FLOATY ? '6' : '4',
    background: isCollapsedOrPushy || opaqueBackground ? theme.color['fill-surface-default'] : `${theme.color['fill-surface-default']}F2`
  };
  if (floatingActive) {
    // `floatingActive` stays true through the close animation (panelInTransition),
    // so the panel keeps its sizing while shrinking
    return Object.assign({}, shared, {
      height: `calc(100% - ${NAV_FLOATING_GAP_VALUE + ($contentMarginBlockStart === '0' ? 0 : NAV_FLOATING_GAP_VALUE)}px)`,
      marginBlockStart: $contentMarginBlockStart !== null && $contentMarginBlockStart !== void 0 ? $contentMarginBlockStart : theme.space['200'],
      marginBlockEnd: theme.space['200'],
      marginInlineEnd: theme.space['200'],
      borderRadius: theme.borderRadius['400'],
      boxShadow: theme.boxShadow['100'],
      border: `${theme.borderWidth['100']} solid ${theme.color['border-core-subtle']}`
    });
  }
  return Object.assign({}, shared, {
    height: '100%'
  }, floating ? {} : {
    borderLeft: !collapsed ? `${theme.borderWidth['100']} solid ${theme.color['border-core-subtle']}` : 'none',
    borderRight: panelType === FLOATY ? `${theme.borderWidth['100']} solid ${theme.color['border-core-subtle']}` : 'none'
  });
});
const Wrapper = styled.span.withConfig({
  displayName: "EPSideToolbar__Wrapper"
})(({
  $withLabel,
  $labelBelow,
  $floating,
  disabled,
  collapsed,
  buttonIsActive,
  theme
}) => {
  const backgroundColor = buttonIsActive && !collapsed ? theme.color['fill-tertiary-pressed'] : theme.color['fill-surface-default'];
  return Object.assign({}, $withLabel ? {
    width: '100%'
  } : {}, $labelBelow ? {
    paddingInline: theme.space['100'],
    paddingBlock: theme.space['200']
  } : {
    padding: theme.space['50']
  }, {
    backgroundColor,
    // Not ideal, but a workaround because the ButtonGroup component renders a
    // dark grey square behind disabled buttons with no way to update the border radius.
    // This hides the tiny corners of gray that poke out behind the button.
    borderRadius: disabled ? theme.space['0'] : $floating ? theme.borderRadius.full : theme.space['100'],
    '&&&:hover': {
      backgroundColor: !disabled && !buttonIsActive ? theme.color['fill-tertiary-hover'] : ''
    }
  });
});
const BetaBadge = () => {
  return /*#__PURE__*/_jsx(View, {
    insetInlineStart: "300",
    insetBlockEnd: "800",
    position: "absolute",
    zIndex: "10",
    children: /*#__PURE__*/_jsx(Badge, {
      use: "beta",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "epSideToolbar.betaBadge"
      })
    })
  });
};
const SidebarButton = ({
  buttonIsActive,
  collapsed,
  dataOnboarding,
  disabled,
  floating,
  iconNode,
  label,
  labelPlacement,
  onClick,
  panelName,
  showBetaBadge
}) => {
  const iconHolder = iconNode && /*#__PURE__*/_jsx(IconHolder, {
    color: disabled ? 'icon-core-disabled' : 'icon-core-default',
    "data-sidebar-button-id": panelName,
    "data-onboarding": dataOnboarding !== null && dataOnboarding !== void 0 ? dataOnboarding : `ep-side-toolbar-${panelName}`,
    "data-campaign-assistant-onboarding-tour": `tour-step-${panelName}`,
    icon: iconNode,
    padding: "100",
    size: floating ? '300' : '500'
  });
  const hasLabel = !!label;
  const isBelow = hasLabel && labelPlacement === 'below';
  return /*#__PURE__*/_jsx(Button, {
    disabled: disabled,
    onClick: onClick,
    marginBlock: isBelow ? undefined : floating ? '100' : '200',
    use: "unstyled",
    children: /*#__PURE__*/_jsx(Wrapper, {
      $withLabel: hasLabel,
      $labelBelow: isBelow,
      $floating: floating,
      disabled: disabled,
      collapsed: collapsed,
      buttonIsActive: buttonIsActive,
      children: hasLabel ? /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        flexDirection: isBelow ? 'column' : 'row',
        gap: "200",
        inlineSize: "100%",
        children: [showBetaBadge && /*#__PURE__*/_jsx(BetaBadge, {}), iconHolder, /*#__PURE__*/_jsx(Text, {
          typography: isBelow ? 'detail-100' : 'body-100',
          textAlign: isBelow ? 'center' : 'start',
          children: label
        })]
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [showBetaBadge && /*#__PURE__*/_jsx(BetaBadge, {}), iconHolder]
      })
    })
  });
};
const SidebarMenuSeparator = styled.div.withConfig({
  displayName: "EPSideToolbar__SidebarMenuSeparator"
})(({
  theme,
  $addMarginBlockEnd,
  $floating
}) => Object.assign({
  height: theme.space['25'],
  backgroundColor: theme.color['border-core-subtle'],
  marginInline: $floating ? 'auto' : theme.space['100']
}, $floating ? {
  inlineSize: theme.space['300'],
  marginBlock: theme.space['250']
} : {
  marginBlockEnd: $addMarginBlockEnd ? theme.space['250'] : '0'
}));
const WhiteVeilAndContent = styled.div.withConfig({
  displayName: "EPSideToolbar__WhiteVeilAndContent"
})(({
  $panelInTransition,
  $cssStyles
}) => Object.assign({
  // We want to mount the child components, but not show them until sidebar
  // currently animating open or close. Rather, we want to render this just as the animation
  // finishes so it shows up just as the veil is being removed.
  visibility: $panelInTransition ? 'hidden' : 'visible',
  height: '100%'
}, $cssStyles));
const WhiteVeilAndContentContainer = _ref2 => {
  let {
      panelInTransition,
      children
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const {
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(WhiteVeilAndContent, {
      $panelInTransition: panelInTransition,
      $cssStyles: cssStyles,
      children: children
    })
  );
};
class EPSideToolbar extends Component {
  constructor(_props) {
    var _props$defaultNavExpa;
    super(_props);
    this.handlePanelTransition = () => {
      this.setState({
        panelInTransition: true
      });
      setTimeout(() => {
        const {
          onContentShown
        } = this.props;
        this.setState({
          panelInTransition: false
        });
        if (onContentShown) {
          onContentShown();
        }
      }, TOGGLED_ANIMATION_DURATION);
    };
    this.onAnimationFrame = newWidth => {
      const {
        onToggleAnimationFrame
      } = this.props;
      if (onToggleAnimationFrame) onToggleAnimationFrame(newWidth, this.animationType);
    };
    this.customToggleAnimation = timestamp => {
      if (!this.sidebarDomNode) {
        return;
      }
      if (!this.animationFrameStart) {
        this.animationFrameStart = timestamp;
      }
      const ratioAnimationComplete = (timestamp - this.animationFrameStart) / TOGGLED_ANIMATION_DURATION;
      if (ratioAnimationComplete < 1) {
        const easingCoefficient = easeInOutSine(ratioAnimationComplete);
        const newSidebarWidth = computeUpdatedWidth(easingCoefficient, this.startSidebarWidth, this.finalSidebarWidth);
        this.sidebarDomNode.style.width = `${newSidebarWidth}px`;
        this.onAnimationFrame(newSidebarWidth);
        this.requestAnimationFrameId = requestAnimationFrame(this.customToggleAnimation);
      } else {
        this.sidebarDomNode.style.width = `${this.finalSidebarWidth}px`;
        this.onAnimationFrame(this.finalSidebarWidth);
        this.handleToggleAnimationEnd();
        this.clearAnimationParameters();
      }
    };
    this.handleToggleAnimationEnd = () => {
      const {
        onToggleAnimationEnd
      } = this.props;
      if (onToggleAnimationEnd) onToggleAnimationEnd();
    };
    this.initSidebarDomNode = () => {
      if (!this.sidebarDomNode) {
        this.sidebarDomNode = this.sidebarRef;
      }
    };
    this.setupToggleAnimationParameters = (finalWidth, previousPanelType, nextPanelType) => {
      if (!this.sidebarRef) {
        return;
      }
      const sidebarRect = this.sidebarRef.getBoundingClientRect();
      if (this.props.onToggleAnimationStart) {
        this.props.onToggleAnimationStart();
      }
      this.initSidebarDomNode();
      if (previousPanelType === FLOATY && nextPanelType === PUSHY) {
        this.startSidebarWidth = 0;
        if (this.sidebarDomNode) {
          this.sidebarDomNode.style.width = '0px';
        }
      } else {
        this.startSidebarWidth = sidebarRect.width;
      }
      this.finalSidebarWidth = finalWidth;
      this.animationType = getPatagoniaPanelAnimationType(previousPanelType, nextPanelType);
    };
    this.setupWidthAnimationParameters = finalWidth => {
      if (this.sidebarRef) {
        const sidebarRect = this.sidebarRef.getBoundingClientRect();
        const sidebarWidth = sidebarRect.width;
        if (this.props.onWidthAnimationStart) {
          this.props.onWidthAnimationStart(sidebarWidth);
        }
        this.initSidebarDomNode();
        this.startSidebarWidth = sidebarWidth;
        this.finalSidebarWidth = finalWidth;
      }
    };
    this.clearAnimationParameters = () => {
      this.animationFrameStart = null;
      this.startSidebarWidth = 0;
      this.finalSidebarWidth = 0;
      this.requestAnimationFrameId = null;
      this.animationType = null;
    };
    this.collapseSideToolbarPanel = previousPanel => {
      this.setupToggleAnimationParameters(0, this.getPanelType(previousPanel), COLLAPSED);
      if (this.requestAnimationFrameId) {
        cancelAnimationFrame(this.requestAnimationFrameId);
      }
      this.requestAnimationFrameId = requestAnimationFrame(this.customToggleAnimation);
    };
    this.openToSideToolbarPanel = (previousPanel, nextPanel) => {
      const {
        sidebarOptions
      } = this.props;
      const nextActivePanel = getPanelFromSidebarOptions(sidebarOptions, nextPanel);
      if (nextActivePanel) {
        this.setupToggleAnimationParameters(nextActivePanel.contentWidth, this.getPanelType(previousPanel), nextActivePanel.panelDisplay.type);
        if (this.requestAnimationFrameId) {
          cancelAnimationFrame(this.requestAnimationFrameId);
        }
        this.requestAnimationFrameId = requestAnimationFrame(this.customToggleAnimation);
      }
    };
    this.renderWhiteVeilAndContent = content => {
      const {
        panelInTransition
      } = this.state;
      const props = this.props;
      const cssTransition = /*#__PURE__*/_jsx(CSSTransitionGroup, {
        transitionName: "white-veil",
        transitionAppear: true,
        transitionEnter: true,
        transitionLeave: true,
        transitionAppearTimeout: 100,
        transitionEnterTimeout: 100,
        transitionLeaveTimeout: 250,
        component: "div",
        children: panelInTransition && /*#__PURE__*/_jsx(FadingVeil, {}, 'white-veil-animation')
      });
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [cssTransition, /*#__PURE__*/_jsx(WhiteVeilAndContentContainer, Object.assign({
          panelInTransition: panelInTransition
        }, props.sidebarContentStyles, {
          children: content
        }))]
      });
    };
    this.updateActivePanel = panelName => {
      const {
        handlePanelChange
      } = this.props;
      handlePanelChange(panelName);
    };
    this.handleSidebarButton = panelName => {
      const {
        collapsed,
        onExpand,
        onCollapse,
        activePanel
      } = this.props;
      if (collapsed) {
        this.updateActivePanel(panelName);
        this.openToSideToolbarPanel('', panelName);
        onExpand === null || onExpand === void 0 || onExpand();
      } else {
        if (panelName === activePanel) {
          onCollapse === null || onCollapse === void 0 || onCollapse();
          this.collapseSideToolbarPanel(activePanel);
          this.updateActivePanel('');
        } else {
          this.openToSideToolbarPanel(activePanel, panelName);
          this.updateActivePanel(panelName);
        }
      }
    };
    this.isInlineExpandableLayout = () => {
      const {
        navLabelLayout = EPSIDE_TOOLBAR_NAV_LABEL_LAYOUT.ICONS_ONLY
      } = this.props;
      return navLabelLayout === EPSIDE_TOOLBAR_NAV_LABEL_LAYOUT.INLINE_EXPANDABLE;
    };
    this.isLabelBelowLayout = () => {
      const {
        navLabelLayout = EPSIDE_TOOLBAR_NAV_LABEL_LAYOUT.ICONS_ONLY
      } = this.props;
      return navLabelLayout === EPSIDE_TOOLBAR_NAV_LABEL_LAYOUT.LABEL_BELOW;
    };
    this.isFloatingAppearance = () => this.props.appearance === EPSIDE_TOOLBAR_APPEARANCE.FLOATING;
    this.renderTrellisIcons = () => {
      const {
        sidebarOptions,
        collapsed,
        activePanel
      } = this.props;
      const isFloating = this.isFloatingAppearance();
      const isLabelBelow = this.isLabelBelowLayout();
      return sidebarOptions.map((option, index) => {
        if (option.type === PANEL) {
          if (option.hideItem || !('iconNode' in option)) {
            return null;
          }
          const buttonIsActive = option.panelName === activePanel;
          const disabled = Boolean(option.disabled);
          const showLabel = isLabelBelow || this.isInlineExpandableLayout() && this.state.isNavExpanded;
          if (showLabel) {
            const labelPlacement = isLabelBelow ? 'below' : 'inline';
            const labeledButton = /*#__PURE__*/_jsx(SidebarButton, {
              buttonIsActive: buttonIsActive,
              collapsed: collapsed,
              disabled: disabled,
              floating: isFloating,
              iconNode: option.iconNode,
              label: option.tooltipLabel,
              labelPlacement: labelPlacement,
              onClick: () => this.handleSidebarButton(option.panelName),
              panelName: option.panelName,
              showBetaBadge: option.showBetaBadge,
              dataOnboarding: option['data-onboarding']
            }, `epSideToolbarIcon-${index}`);
            return /*#__PURE__*/_jsx(Tooltip, {
              placement: "right",
              title: option.disabledTooltipLabel,
              disabled: !(disabled && option.disabledTooltipLabel),
              children: labeledButton
            }, `epSideToolbarIcon-${index}`);
          }
          const tooltipTitle = disabled && option.disabledTooltipLabel ? option.disabledTooltipLabel : option.tooltipLabel;
          return /*#__PURE__*/_jsx(Tooltip, {
            placement: "right",
            title: tooltipTitle,
            disabled: !tooltipTitle,
            children: /*#__PURE__*/_jsx(SidebarButton, {
              buttonIsActive: buttonIsActive,
              collapsed: collapsed,
              disabled: disabled,
              floating: isFloating,
              iconNode: option.iconNode,
              onClick: () => this.handleSidebarButton(option.panelName),
              panelName: option.panelName,
              showBetaBadge: option.showBetaBadge,
              dataOnboarding: option['data-onboarding']
            })
          }, `epSideToolbarIcon-${index}`);
        } else {
          return /*#__PURE__*/_jsx(SidebarMenuSeparator, {
            $addMarginBlockEnd: false,
            $floating: isFloating
          }, index);
        }
      });
    };
    this.panelToRender = () => {
      const {
        sidebarOptions,
        activePanel
      } = this.props;
      const DEFAULT_PANEL = {
        content: '',
        contentWidth: 0,
        panelDisplay: {
          type: COLLAPSED,
          showScroll: false,
          opaqueBackground: false
        }
      };
      const panelToRender = getPanelFromSidebarOptions(sidebarOptions, activePanel);
      return panelToRender || DEFAULT_PANEL;
    };
    this.renderSidebarContent = () => {
      const {
        activePanel
      } = this.props;
      if (!activePanel) {
        return null;
      }
      return this.renderWhiteVeilAndContent(this.panelToRender().content);
    };
    this.renderNavToggleButton = () => {
      if (!this.isInlineExpandableLayout()) {
        return null;
      }
      const {
        isNavExpanded
      } = this.state;
      return /*#__PURE__*/_jsx(View, {
        position: "absolute",
        insetBlockEnd: "0px",
        insetInlineEnd: "0px",
        padding: "100",
        paddingBlockEnd: "300",
        children: /*#__PURE__*/_jsx(Button, {
          use: "unstyled",
          "aria-label": isNavExpanded ? I18n.text('epSideToolbar.collapseSidebar') : I18n.text('epSideToolbar.expandSidebar'),
          onClick: () => {
            var _this$props$onNavTogg, _this$props;
            (_this$props$onNavTogg = (_this$props = this.props).onNavToggle) === null || _this$props$onNavTogg === void 0 || _this$props$onNavTogg.call(_this$props, !isNavExpanded);
            this.setState({
              isNavExpanded: !isNavExpanded
            });
          },
          children: /*#__PURE__*/_jsx(IconHolder, {
            icon: isNavExpanded ? SidebarLeftCollapseIcon : SidebarLeftExpandIcon,
            color: "icon-core-default",
            size: "500"
          })
        })
      });
    };
    this.wrapInNavCard = node => this.isFloatingAppearance() ?
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(NavToolbarCard, {
      $navCardMarginBlockStart: this.props.navCardMarginBlockStart,
      "data-test-id": "ep-sidetoolbar-nav-card",
      children: node
    }) : node;
    this.renderNavItems = () => {
      const isFloating = this.isFloatingAppearance();
      const isInlineExpandable = this.isInlineExpandableLayout();
      if (isInlineExpandable && this.state.isNavExpanded) {
        return this.renderExpandableLabeledNav();
      }
      if (this.isLabelBelowLayout()) {
        return this.wrapInNavCard( /*#__PURE__*/_jsx(View, {
          "data-test-id": "ep-sidetoolbar-nav-items-container",
          paddingInline: "100",
          paddingBlock: "400",
          inlineSize: LABEL_BELOW_NAV_INLINE_SIZE,
          overflow: "auto",
          blockSize: "100%",
          children: /*#__PURE__*/_jsx(ButtonGroup, {
            "data-test-id": "ep-sidetoolbar-nav-items",
            gap: isFloating ? '0' : '300',
            orientation: "vertical",
            inlineSize: "100%",
            children: this.renderTrellisIcons()
          })
        }));
      }
      return this.wrapInNavCard( /*#__PURE__*/_jsxs(View, {
        "data-test-id": "ep-sidetoolbar-nav-items-container",
        paddingInline: "50",
        position: isInlineExpandable ? 'relative' : undefined,
        blockSize: isInlineExpandable ? '100%' : undefined,
        children: [/*#__PURE__*/_jsx(ButtonGroup, {
          "data-test-id": "ep-sidetoolbar-nav-items",
          gap: isFloating ? '0' : '250',
          marginInline: "100",
          paddingInline: "25",
          orientation: "vertical",
          children: this.renderTrellisIcons()
        }), this.renderNavToggleButton()]
      }));
    };
    this.renderExpandableLabeledNav = () => {
      return this.wrapInNavCard( /*#__PURE__*/_jsxs(View, {
        position: "relative",
        blockSize: "100%",
        inlineSize: EXPANDED_NAV_INLINE_SIZE,
        children: [/*#__PURE__*/_jsx(View, {
          "data-test-id": "ep-sidetoolbar-nav-items-container",
          paddingInline: "50",
          children: /*#__PURE__*/_jsx(ButtonGroup, {
            "data-test-id": "ep-sidetoolbar-nav-items",
            gap: "250",
            inlineSize: "100%",
            marginInline: "100",
            paddingInlineStart: "25",
            paddingInlineEnd: "400",
            orientation: "vertical",
            children: this.renderTrellisIcons()
          })
        }), this.renderNavToggleButton()]
      }));
    };
    this.state = {
      panelInTransition: false,
      isNavExpanded: (_props$defaultNavExpa = _props.defaultNavExpanded) !== null && _props$defaultNavExpa !== void 0 ? _props$defaultNavExpa : true
    };
    this.sidebarDomNode = null;
    this.animationFrameStart = null;
    this.startSidebarWidth = 0;
    this.finalSidebarWidth = 0;
    this.requestAnimationFrameId = null;
    this.animationType = null;
    this.sidebarRef = null;
    this.customWidthAnimation = this.customWidthAnimation.bind(this);
  }
  componentDidMount() {
    this.initSidebarDomNode();
    if (!this.sidebarDomNode) {
      return;
    }
    const sidebarRefInlineWidth = this.sidebarDomNode.style.width;
    const currentPanel = this.panelToRender();
    const currentExpectedWidth = currentPanel.contentWidth;

    // Ensure our inline styles are set for the first time the sidebar is toggled
    if (sidebarRefInlineWidth === '') {
      // Use requestAnimationFrame to make sure we aren't triggering any reflows
      window.requestAnimationFrame(() => {
        if (this.sidebarDomNode) {
          this.sidebarDomNode.style.width = `${currentExpectedWidth}px`;
        }
      });
    }
  }
  componentDidUpdate(prevProps) {
    const {
      activePanel: currentActivePanel,
      sidebarOptions
    } = this.props;
    const previousActivePanel = prevProps.activePanel;
    const currentPanelIsEmpty = getIsPanelEmpty(currentActivePanel);
    const activePanelHasChanged = previousActivePanel !== currentActivePanel;
    if (activePanelHasChanged) {
      this.handlePanelTransition();
      if (currentPanelIsEmpty) {
        this.collapseSideToolbarPanel(previousActivePanel);
      } else {
        this.openToSideToolbarPanel(previousActivePanel, currentActivePanel);
      }
    } else if (!currentPanelIsEmpty && getWidthForPanel(sidebarOptions, currentActivePanel) !== getWidthForPanel(prevProps.sidebarOptions, previousActivePanel)) {
      this.animateActivePanelWidth(getWidthForPanel(sidebarOptions, currentActivePanel));
    }
  }
  getPanelType(panelName) {
    const {
      sidebarOptions
    } = this.props;
    const panel = getPanelFromSidebarOptions(sidebarOptions, panelName);
    return panel ? panel.panelDisplay.type : COLLAPSED;
  }
  handleWidthAnimationEnd() {
    const {
      onWidthAnimationEnd
    } = this.props;
    if (onWidthAnimationEnd) onWidthAnimationEnd();
  }
  onWidthAnimationFrame(easingCoefficient) {
    const {
      onWidthAnimationFrame
    } = this.props;
    if (onWidthAnimationFrame) onWidthAnimationFrame(easingCoefficient);
  }
  customWidthAnimation(timestamp) {
    if (!this.sidebarDomNode) {
      return;
    }
    if (!this.animationFrameStart) {
      this.animationFrameStart = timestamp;
    }
    const ratioAnimationComplete = (timestamp - this.animationFrameStart) / TOGGLED_ANIMATION_DURATION;
    if (ratioAnimationComplete < 1) {
      const easingCoefficient = easeInOutSine(ratioAnimationComplete);
      const newSidebarWidth = computeUpdatedWidth(easingCoefficient, this.startSidebarWidth, this.finalSidebarWidth);
      this.sidebarDomNode.style.width = `${newSidebarWidth}px`;
      this.onWidthAnimationFrame(easingCoefficient);
      this.requestAnimationFrameId = requestAnimationFrame(this.customWidthAnimation);
    } else {
      this.sidebarDomNode.style.width = `${this.finalSidebarWidth}px`;
      this.onWidthAnimationFrame(null);
      this.handleWidthAnimationEnd();
      this.clearAnimationParameters();
    }
  }
  animateActivePanelWidth(finalWidth) {
    this.setupWidthAnimationParameters(finalWidth);
    if (this.requestAnimationFrameId) {
      cancelAnimationFrame(this.requestAnimationFrameId);
    }
    this.requestAnimationFrameId = requestAnimationFrame(this.customWidthAnimation);
  }

  //sidebarOptions should be passed as for example see builder-demo-app's HomeContainer

  // When floating, the nav renders as a detached card; otherwise it renders bare,
  // exactly as before.
  render() {
    const _this$props2 = this.props,
      {
        collapsed,
        appearance,
        contentMarginBlockStart
      } = _this$props2,
      rest = _objectWithoutPropertiesLoose(_this$props2, _excluded3);
    const isFloating = appearance === EPSIDE_TOOLBAR_APPEARANCE.FLOATING;
    const panelToRender = this.panelToRender();
    const panelType = panelToRender.panelDisplay.type;
    const expandedWidth = panelToRender.contentWidth;
    const showScroll = panelToRender.panelDisplay.showScroll;
    const opaqueBackground = panelToRender.panelDisplay.opaqueBackground;
    // Keep the panel's floating chrome through the open/close animation
    // (panelInTransition), then drop it at rest so a collapsed panel leaves no
    // sliver.
    const floatingActive = isFloating && (!collapsed || this.state.panelInTransition);
    return /*#__PURE__*/_jsxs(SidebarContainer, Object.assign({
      collapsed: collapsed,
      "data-test-id": "ep-sidetoolbar",
      panelType: panelType,
      opaqueBackground: opaqueBackground,
      floating: isFloating
    }, rest, {
      children: [this.renderNavItems(), /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(SidebarContent, {
          ref: ref => {
            this.sidebarRef = ref;
          },
          panelType: panelType,
          showScroll: showScroll,
          opaqueBackground: opaqueBackground,
          collapsed: collapsed,
          floating: isFloating,
          floatingActive: floatingActive,
          $contentMarginBlockStart: contentMarginBlockStart,
          "data-test-id": "ep-sidetoolbar-content",
          "data-onboarding": "ep-sidetoolbar-content",
          "data-campaign-assistant-onboarding-tour": "tour-step-toolbar-content",
          "data-test-width": expandedWidth,
          children: this.renderSidebarContent()
        })
      })]
    }));
  }
}
EPSideToolbar.defaultProps = {
  expandedWidth: DEFAULT_EXPANDED_WIDTH
};
export const TrellisEPSideToolbar = EPSideToolbar;
export default EPSideToolbar;